import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
    teacherApi,
    type Teacher,
    type TeacherFilters,
    type CreateTeacherData,
    type UpdateTeacherData,
} from "@/api/teacher";

export const useTeacherStore = defineStore("teacher", () => {
    // State
    const teachers = ref<Teacher[]>([]);
    const currentTeacher = ref<Teacher | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);
    const search = ref("");
    const specialization = ref("");
    const status = ref("");
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);

    // Getters
    const activeTeachers = computed(() =>
        teachers.value.filter((teacher) => teacher.is_homeroom),
    );

    const teachersBySpecialization = computed(() => {
        const grouped: Record<string, Teacher[]> = {};
        teachers.value.forEach((teacher) => {
            const spec = teacher.specialization || "Uncategorized";
            if (!grouped[spec]) grouped[spec] = [];
            grouped[spec].push(teacher);
        });
        return grouped;
    });

    // Actions
    const fetchTeachers = async (filters: TeacherFilters = {}) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await teacherApi.getAll({
                page: page.value,
                per_page: perPage.value,
                search: search.value || undefined,
                specialization: specialization.value || undefined,
                status: status.value || undefined,
                ...filters,
            });

            teachers.value = response.data?.items || response.data || [];
            meta.value = response.data?.meta || null;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch teachers";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchTeacher = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            currentTeacher.value = (await teacherApi.getById(id)).data;
            return currentTeacher.value;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch teacher";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const createTeacher = async (data: CreateTeacherData) => {
        loading.value = true;
        error.value = null;

        try {
            const newTeacher = await teacherApi.create(data);
            // Refresh the list to ensure consistency
            await fetchTeachers();
            return newTeacher;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to create teacher";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateTeacher = async (id: string, data: UpdateTeacherData) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedTeacher = await teacherApi.update(id, data);
            const index = teachers.value.findIndex((t) => t.id === id);
            if (index !== -1) {
                teachers.value[index] = updatedTeacher.data;
            }
            if (currentTeacher.value?.id === id) {
                currentTeacher.value = updatedTeacher.data;
            }
            return updatedTeacher.data;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to update teacher";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deleteTeacher = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            await teacherApi.delete(id);
            teachers.value = teachers.value.filter((t) => t.id !== id);
            if (currentTeacher.value?.id === id) {
                currentTeacher.value = null;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to delete teacher";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchHomeroomTeachers = async () => {
        loading.value = true;
        error.value = null;

        try {
            const homeroomTeachers = await teacherApi.getHomeroomTeachers();
            console.info(homeroomTeachers);
            return homeroomTeachers;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch homeroom teachers";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const assignHomeroom = async (teacherId: string, classId: string) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedTeacher = await teacherApi.assignHomeroom(
                teacherId,
                classId,
            );
            const index = teachers.value.findIndex((t) => t.id === teacherId);
            if (index !== -1) {
                teachers.value[index] = updatedTeacher.data;
            }
            return updatedTeacher.data;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to assign homeroom";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const removeHomeroom = async (teacherId: string) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedTeacher = await teacherApi.removeHomeroom(teacherId);
            const index = teachers.value.findIndex((t) => t.id === teacherId);
            if (index !== -1) {
                teachers.value[index] = updatedTeacher.data;
            }
            return updatedTeacher.data;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to remove homeroom";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const setPage = (newPage: number) => {
        if (!meta.value) return;
        if (newPage < 1 || newPage > meta.value.last_page) return;

        page.value = newPage;
        fetchTeachers();
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1;
        fetchTeachers();
    };

    const resetFilters = () => {
        page.value = 1;
        perPage.value = 10;
        search.value = "";
        specialization.value = "";
        status.value = "";
        fetchTeachers();
    };

    return {
        // State
        teachers,
        currentTeacher,
        loading,
        error,
        page,
        perPage,
        search,
        specialization,
        status,
        meta,

        // Getters
        activeTeachers,
        teachersBySpecialization,

        // Actions
        fetchTeachers,
        fetchTeacher,
        createTeacher,
        updateTeacher,
        deleteTeacher,
        fetchHomeroomTeachers,
        assignHomeroom,
        removeHomeroom,
        clearError,
        setPage,
        setPerPage,
        resetFilters,
    };
});
